import type { CSSProperties } from 'react';
import type { RoleMember } from '../../services/roles';
import { AssigneeAvatar } from './AssigneeAvatar';
import { AssigneeName } from './AssigneeName';
import { getAvatarColor } from './assigneeSelectorHelpers';

interface AssigneeMemberRowProps {
    member: RoleMember;
    isSelf: boolean;
    isActive: boolean;
    onSelect: () => void;
}

/** One pickable row - the active row uses its hashed color as the
 *  background (inline style; Tailwind can't express an arbitrary color). */
export function AssigneeMemberRow({ member, isSelf, isActive, onSelect }: AssigneeMemberRowProps) {
    const color = getAvatarColor(member.fullname);

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onSelect();
            }}
            className={`flex items-center gap-2.5 w-full text-xs font-semibold py-[9px] px-3.5 rounded-lg transition-all duration-100 ${
                isActive
                    ? 'text-white ring-2 ring-offset-1'
                    : 'text-gray-700 bg-gray-50 hover:bg-gray-100 hover:scale-[1.02]'
            }`}
            style={
                isActive
                    ? ({ backgroundColor: color, '--tw-ring-color': color } as CSSProperties)
                    : undefined
            }
        >
            <AssigneeAvatar
                name={member.fullname}
                color={isActive ? 'rgba(255,255,255,0.25)' : color}
                size="sm"
            />
            <AssigneeName fullname={member.fullname} isMe={isSelf} />
            {isActive && (
                <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 14 14" fill="none">
                    <path
                        d="M2 7L5.5 10.5L12 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </button>
    );
}
